import $ from "jquery";
import { _ } from "extensions/underscore";
import { captureException } from "extensions/sentry";
import Radio from "backbone.radio";

import strings from "common/strings";

import { makeMnErrorView } from "common/components/error";
import { FETCH_SUMMARY_DATA } from "common/actions/dashboard";

import {
    UnknownBodyType,
    FetchErrorRetry
} from "common/helpers/errors";

import { references } from "common/helpers/references";
import { ParameterizedTransformCollection } from "common/collections/parameterizedTransformCollection";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";

import { DefaultView } from "../defaultView";
import template from "./doublePivotView.jst";
import { DoublePivotBodyContents } from "./doublePivotBodyContents";

import { ChartBody } from "./chartBody";
import { PieChartBody } from "./pieChartBody";
import { TableBody } from "./tableBody";

import { basicDrillThrough, getDateRangesFromQueryParams } from "../widgetHelper";

export const DoublePivotView = DefaultView.extend({
    type: "doublePivot",
    template,
    regions: {
        tabs: ".content"
    },
    ui: {
        widgetTitle: ".header .title",
        ...DefaultView.prototype.ui
    },
    
    events: {
        "click @ui.widgetTitle": "drillDownCompare",
        ...DefaultView.prototype.events
    },
    
    /**
     * When event fired in a tab, send the event along with secondary pivot value to this method
     * @param event
     * @param value
     */

    drillDownCompare() {
        // Don't drilldown if widget is moving, resizeing, or event is triggered when selecting text
        if (this.isClicking === false || this.isResizing || this.isDragging) {
            return;
        }
        
        const props = {
            urlRoot: this.model.subtypeModel.get("urlRoot"),
            dateRanges: getDateRangesFromQueryParams(this.collection.queryParams),
            widgetQualifiers: new Qualifiers({ value: this.filterInfo.mergedString })
        };
        basicDrillThrough(props);
    },
    
    getPrimaryPivotQualifierFromEvent(e) {
        const model = this.collection.at($(e.currentTarget).closest(".tab-pane").index());
        const primaryPivot = _.first(this.model.getOption("pivot")) || [];
        
        return `${primaryPivot}:${model.get(Radio.channel("main").request("get:getters").findPivotKey(primaryPivot))}`;
    },
    
    attributes(...args) {
        return {
            ...DefaultView.prototype.attributes.call(this, ...args),
            "gs-max-h": 10,
            "gs-min-w": 6,
            "gs-min-h": 3,
            "gs-max-w": 12
        };
    },
    
    initialize() {
        const qString = this.model.get("qualifiers");
        const initialQ = this.model.subtypeModel.get("doublePivot").reactProps.initialFilter || "";
        if (initialQ && !qString) {
            this.model.set("qualifiers", initialQ);
        }
        
        this.setDataCollection();
    },
    
    setDataCollection(...args) {
        DefaultView.prototype.setDataCollection.apply(this, args);
        
        const [first_pivot, second_pivot] = this.model.getOption("pivot");
        const first_pivot_key = Radio.channel("main").request("get:getters").findPivotKey(first_pivot);
        const second_pivot_key = Radio.channel("main").request("get:getters").findPivotKey(second_pivot);
        
        this.collection.fetch = function() {
            this.trigger("before:fetched", this);
            this.trigger("react_fetched", this);
        };
        
        this.listenTo(this.collection, "react_fetched", () => {
            const queryParams = this.collection.queryParams;
            const { start, end } = queryParams;
            // Put things in the same format as the compare function so in future easily incorporated
            const widgetQualifiers = new Qualifiers({ value: this.filterInfo.mergedString });
            const endpoint = this.model.subtypeModel.get("api_endpoint");
            if (this.existingPromiseRequest) {
                this.existingPromiseRequest.cancel();
            }
            
            this.existingPromiseRequest = FETCH_SUMMARY_DATA({
                url: _.isFunction(endpoint) ? endpoint(first_pivot_key) : endpoint,
                start,
                end,
                q: widgetQualifiers
            }).then((data) => {
                this.removeSpinners();
                const options = {
                    data,
                    dateRanges: getDateRangesFromQueryParams(queryParams),
                    first_pivot_key,
                    second_pivot_key,
                    widgetContainer: this.el,
                    widgetType: this.model.get("widgetType"),
                    widgetSubType: this.model.get("widgetSubtype"),
                    widgetQualifiers,
                    subModel: this.model.subtypeModel
                };
                
                this.showChildView("tabs", <DoublePivotBodyContents {...options}/>, {
                    dumb: true
                });
            }, (error) => {
                if (!this.isDestroyed()) {
                    this.removeSpinners();
                    this.showChildView("tabs", makeMnErrorView(new FetchErrorRetry({
                        message: "Please refresh widget as data could not load",
                        customActions: [{
                            className: "retry-widget",
                            label: "Refresh widget"
                        }],
                        hideDetails: true,
                        custom: true,
                        originalError: error
                    }), true, false));
                }
            });
        });
    },
    
    getLoadingChildren() {
        return this.$(".content");
    },
    
    /**
     * Take a model and generate a layout tab based on its properties
     * @param {Backbone.Model} model
     * @private
     */
    _makeTab(model, index, pivot) {
        const primaryPivot = pivot[0];
        let secondaryPivot = pivot[1];
        const primaryPivotRef = model.get(Radio.channel("main").request("get:getters").findPivotKey(primaryPivot));
        const primaryPivotAlias = references.toAlias(primaryPivotRef, primaryPivot);
        const total = model.get("total");
        const displayBadge = this.model.showBadgeInTabNavi();
        const dataGetter = this.model.subtypeModel.getClone("doublePivot").dataGetter;
        const items = (model.get(Radio.channel("main").request("get:getters").findPivotKey(secondaryPivot)) || []).sort((a, b) => dataGetter(b) - dataGetter(a));
        const collection = new ParameterizedTransformCollection(items);
        const tooltipTemplate = displayBadge ? "$pivot ($total)" : "$pivot";
        
        // Since we artificially (from existing sources) create this collection, add `fetched` true
        collection.fetched = true;
        collection.queryParams = model.collection.queryParams;
        
        let secondaryPivotString = Radio.channel("main").request("get:getters").findPivotName(secondaryPivot);
        if (["sales", "refunds"].includes(this.model.get("widgetSubtype")) && secondaryPivot === "currency") {
            secondaryPivotString = strings.stl_currency;
        }
        
        if (secondaryPivot === "currency") {
            secondaryPivot = "currency_alias";
        }
        
        return {
            name: primaryPivotAlias + (displayBadge ? `<span class='badge'>${total}</span>` : ""),
            tooltip: _.interpolate(tooltipTemplate, {
                pivot: primaryPivotAlias,
                total
            }),
            active: index === 0,
            hideCloseIcon: true,
            view: this.getViewClass(),
            viewOptions: {
                collection,
                model,
                key: Radio.channel("main").request("get:getters").findPivotKey(secondaryPivot),
                secondaryPivot,
                dataGetter,
                primaryPivotAlias,
                primaryPivot,
                primaryPivotRef,
                secondaryPivotString,
                widget: this
            }
        };
    },
    
    getAvailableHeight(offset) {
        const $el = this.$el;
        const parentHeight = $el.outerHeight();
        const tabsNaviHeight = $el.find(".nav-tabs").outerHeight(true);
        const summaryHeight = $el.find(".summary").outerHeight(true);
        const parentHeaderHeight = $el.find(".header").outerHeight(true);
        
        return Number.parseInt(parentHeight - tabsNaviHeight - summaryHeight - parentHeaderHeight - (offset || 0), 10);
    },
    
    getViewClass() {
        const bodyType = this.model.subtypeModel.getClone("doublePivot").bodyType;
        
        const bodyTypeToView = {
            table: TableBody,
            chart: ChartBody,
            pie: PieChartBody
        };
        
        if (Object.prototype.hasOwnProperty.call(bodyTypeToView, bodyType) === false) {
            captureException(new UnknownBodyType({
                message: "Not supported body type",
                extra: {
                    bodyType
                }
            }));
        }
        
        return bodyTypeToView[bodyType];
    },
    
    makeWidgetTitle() {
        // Starting with Double Pivot widget, I think it's better to keep full title in widget model, rather than create
        // it based on model properties
        return this.model.get("title");
    },
    
    showErrorMessage() {
        this.addWidgetTitle();
        if (this.getRegion("tabs")) {
            this.showChildView("tabs", makeMnErrorView(new FetchErrorRetry({
                message: "Please refresh widget as data could not load",
                customActions: [{
                    className: "retry-widget",
                    label: "Refresh widget"
                }],
                hideDetails: true,
                custom: true
            }), true, false));
        }
    },
    
    // Called from parent gridstack:init
    onGridstackInit() {
        this.addWidgetTitle();
    },
    
    onDestroy(...args) {
        DefaultView.prototype.onDestroy.call(this, ...args);
        this.tabLayout = null;
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
    }
});
